import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataGrid from '../../../../table/DataGrid'
import { Checkbox, Chip, Skeleton, Typography, FormControlLabel, Radio } from '@mui/material'
import { checkedUserIds } from '../../../../../store/actions/dataEntrySurveyBuilderActions'
import FilterButtonClasses from '../../../Assessment/Classes/Filter/Filter'
import useYears from '../../../../../hooks/setting/useYears'

const TableClass = ({ isSaving, list = [], dataYears, included, setIncluded }) => {
  const dispatch = useDispatch()

  const permissions = useSelector((state) => state?.session?.permissions?.dataentrysurveys ?? {})
  const skip = useSelector((state) => state?.datatable?.surveyClassList?.skip ?? 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.surveyClassList?.rowsPerPage ?? 100)
  const columnsTable = useSelector((state) => state?.datatable?.surveyClassList?.columnsTable ?? [])
  const module = useSelector((state) => state.dataEntrySurveyBuilder || {})
  const surveysBuilder = module?.surveysBuilder ?? {}
  const selectedTemplate = surveysBuilder?.template || null
  const { checkedIds } = surveysBuilder
  const totalFilter = surveysBuilder?.totalFilterList || []
  const loading = surveysBuilder?.loading
  const totalRecords = surveysBuilder?.totalList || 0

  const { getYears } = useYears()

  const onChecked = (ids) => dispatch(checkedUserIds(ids))

  const onCheckedAll = (checked) => {
    let idsToCheck = []

    if (checked && checkedIds.length !== totalRecords) {
      idsToCheck = totalFilter.map((item) => String(item._id))
    }

    onChecked(idsToCheck)
  }

  const onCheckOne = (id, checked) => {
    if (checked) {
      onChecked([...checkedIds, id])
    } else {
      onChecked(checkedIds?.filter((item) => item !== id))
    }
  }

  useEffect(() => {
    getYears()

    // eslint-disable-next-line
  }, [])

  const shouldDisableFilter = !selectedTemplate || loading
  return (
    <div className='absolute inset-0 bottom-8'>
      <div className='h-16 flex items-center justify-start gap-2'>
        <FilterButtonClasses type='surveyClassList' />
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Show:
        </Typography>
        <div className='flex items-center gap-2'>
          <FormControlLabel
            control={
              <Radio
                checked={included === 'all'}
                name='all'
                value={true}
                onChange={() => setIncluded('all')}
                color='primary'
                disabled={shouldDisableFilter || checkedIds?.length === 0}
              />
            }
            label='All'
          />
          <FormControlLabel
            control={
              <Radio
                checked={included === 'included'}
                name='included'
                value={false}
                onChange={() => setIncluded('included')}
                color='primary'
                disabled={shouldDisableFilter || checkedIds?.length === 0}
              />
            }
            label='Included'
          />
          <FormControlLabel
            control={
              <Radio
                checked={included === 'not-included'}
                name='not-included'
                value={false}
                onChange={() => setIncluded('not-included')}
                color='primary'
                disabled={shouldDisableFilter || checkedIds?.length === 0}
              />
            }
            label='Not included'
          />
        </div>
      </div>
      <div className='top-16 absolute inset-0'>
        <DataGrid
          className='survey-builder-table'
          id='table-user-survey'
          rows={list.map((row, index) => {
            const yearLevel = dataYears?.filter((y) => row.yearLevel?.includes(y._id))
            const levels = typeof row?.yearLevel?.[0] === 'string' ? yearLevel : row.yearLevel

            return {
              ...row,
              index: index + 1,
              yearLevel: (
                <div className='flex flex-wrap items-center gap-1'>
                  {levels?.map((yearLevel) => (
                    <Chip
                      key={`user-${row._id}-yearLevel-${yearLevel?._id}`}
                      size='small'
                      label={yearLevel?.name}
                      variant='outlined'
                    />
                  ))}
                </div>
              ),
              code: row.code || '',
              teachers: (
                <div className='flex flex-wrap items-center gap-1'>
                  {row?.teachers?.map((teacher) => (
                    <Chip
                      key={`user-${row._id}-teacher-${teacher?._id}`}
                      size='small'
                      label={`${teacher?.preferredName || teacher?.firstName} ${teacher?.lastName}`}
                      variant='outlined'
                    />
                  ))}
                </div>
              ),
              checkIds: (
                <Checkbox
                  id={`checkbox-id-user-${row._id}`}
                  checked={checkedIds.includes(row._id)}
                  onChange={(e) => onCheckOne(row._id, e.target.checked)}
                  color='primary'
                  disabled={loading || isSaving}
                />
              )
            }
          })}
          loading={loading}
          labelNoRows='No class were found'
          nameModule='surveyClassList'
          headers={[...columnsTable]}
          totalRows={totalRecords}
          skip={skip}
          rowsPerPage={rowsPerPage}
          orderByField='code'
          actionsSetting={
            <>
              <Typography color='primary' className='font-bold px-2'>
                {loading ? <Skeleton width={'190px'} /> : 'Please select the classes this survey is about.'}
              </Typography>
              {loading ? (
                <Skeleton width={'100px'} />
              ) : (
                checkedIds.length > 0 && (
                  <Chip
                    className='min-w-100px'
                    align='right'
                    label={`${checkedIds.length} class(es) selected`}
                    color='primary'
                    size='small'
                    variant='outlined'
                  ></Chip>
                )
              )}
            </>
          }
          columns={[
            {
              label: '#',
              key: 'index',
              align: 'center',
              width: 10,
              show: true,
              hide: false
            },
            {
              show: permissions['list'],
              key: 'checkIds',
              align: 'center',
              width: 15,
              label: (
                <Checkbox
                  id={`checkbox-id-user-header`}
                  checked={checkedIds.length > 0 && checkedIds.length === totalRecords}
                  indeterminate={checkedIds.length > 0 && checkedIds.length < totalRecords}
                  onChange={(e) => onCheckedAll(e.target.checked)}
                  color='primary'
                  disabled={loading || isSaving}
                />
              )
            },
            ...columnsTable
          ]}
        />
      </div>
    </div>
  )
}

export default TableClass
